/* System defined core library */
import { ChangeDetectorRef, Component, DoCheck, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

/* User defined library/components */
import { SelectedExamDetailService } from '../../services/selected-exam-detail.service';

/* User defined enums/constants */
import { TargetFlow } from '../../../../shared/constants/scheduling.constant';


@Component({
  selector: 'app-exam-feature',
  templateUrl: './exam-feature.component.html',
  styleUrls: ['./exam-feature.component.scss'],
})
export class ExamFeatureComponent implements DoCheck, OnInit {

  /* User defined private/public variables  */
  public selectedAll: boolean;
  public overlapsDate: string;
  public overlapsFromDate: string;
  public overlapsToDate: string;
  public isBackToBackRequired: boolean;
  public form: FormGroup;
  private _indeterminate: boolean;


  /* Input-OutPut parameters decalartions */
  @Input() public isSelectAllCkd: boolean;
  @Input() public targetFlow: string;
  @Input() public appointmentData: any;
  @Input() public examCount: number;
  @Input() public isSelectAllApplicable: boolean = true;
  @Input() public multipleSets: boolean;
  @Input() public indeterminate: boolean;
  @Input() public isSetSchedulable: boolean = true;
  @Input() public scheduleMethodError: boolean = false;

  @Output() allExamSelected = new EventEmitter<boolean>();
  @Output() backToBackSelected = new EventEmitter<boolean>();

  /* Service subscriptions */
  overlapsDate$: Subscription;
  overlapsFromDate$: Subscription;
  overlapsToDate$: Subscription;
  translateLanguage$: Subscription;

  /* Constructor declaration with required injected services */
  constructor(
    private chRef: ChangeDetectorRef,
    private fb: FormBuilder,
    private selectedExamDetailService: SelectedExamDetailService,
    private translate: TranslateService
  ) {
    this.overlapsDate$ = selectedExamDetailService.overlapsDate.subscribe(
      overlapsDate => {
        this.overlapsDate = overlapsDate;
      });

    this.isBackToBackRequired = this.multipleSets ? this.isSelectAllApplicable : this.selectedExamDetailService.isBackToBackRequired;
    translate.setDefaultLang(this.selectedExamDetailService.defaultLanguageCode);
    this.translateLanguage$ = selectedExamDetailService.languageCode.subscribe(
      languageCode => {
        translate.use(languageCode);
      }
    );

    this.overlapsFromDate$ = selectedExamDetailService.overlapsFromDate.subscribe(
      overlapsFromDate => {
        if (overlapsFromDate) {
          this.overlapsFromDate = overlapsFromDate;
        }
      }
    );

    this.overlapsToDate$ = selectedExamDetailService.overlapsToDate.subscribe(
      overlapsToDate => {
        this.overlapsToDate = overlapsToDate;
      }
    );
  }

  /* Initialize the component & call required methods when component loaded first time. */
  ngOnInit() {
    this.form = this.fb.group({
      'selectAllExams': new FormControl()
    });
  }

  /* Actions to run on every tick, runs more frequentlythan ngOnChanges. Don't add a bunch of logic or the page will grind to a halt */
  ngDoCheck() {
    // Default functionality of [indeterminate] sets the selected to true when the checkbox is checked
    // when not all exams are schedulable select all is checked=false so we need to override it
    if (this.isSetSchedulable) {
      this.form.controls['selectAllExams'].enable();
    } else {
      this.form.controls['selectAllExams'].disable();
    }
    this.form.controls['selectAllExams'].setValue(this.isSelectAllCkd);
    // after recoupling the indeterminate value in onSelectAll we need to set it back to the value we want
    this._indeterminate = this.indeterminate;
    // after changing the selected and indeterminate  we need to run the change detection again
    this.chRef.detectChanges();
  }

  /* Emit whether select all check box is checked or not */
  onSelectAll(targetObject: any): void {
    // Default functionality of [indeterminate] sets the value to false when the checkbox is checked
    // if the variable that [indeterminate] is set by in the template does not get updated too then the value gets uncoupled from the template
    // therefore, we have to update the local variable and do a change detection to recouple the values
    this._indeterminate = false;
    this.chRef.detectChanges();
    if (this.isSetSchedulable) {
      this.allExamSelected.emit(targetObject.target.checked);
    }
  }

  /* Emit whether BackToBack check box is checked or not */
  onBackToBackSelected(targetObject: any): void {
    this.backToBackSelected.emit(targetObject.target.checked);
  }

  /* Hide the select all checkbox */
  checkIfDisabled(): boolean {
    return this.multipleSets ? false : (this.targetFlow == 'Lpi' && this.appointmentData.every((appt: any) => appt.status == 'cancelled')
      || (this.isBackToBackRequired && (
        (this.targetFlow === TargetFlow.Schedule && this.selectedExamDetailService.scheduleSetAttributeCode)
        || ((this.targetFlow === TargetFlow.Reschedule || this.targetFlow === TargetFlow.Lpi) && this.selectedExamDetailService.rescheduleSetAttributeCode)
        || ((this.targetFlow === TargetFlow.Cancel || this.targetFlow === TargetFlow.Lpi) && this.selectedExamDetailService.cancelSetAttributeCode))));
  }

  isIndeterminate() {
    return this.indeterminate;
  }
}
